/**
 * useEmailCredentials Hook
 * Main workflow orchestration hook for email credentials
 */
import { useState, useCallback, useMemo } from "react";
import { useHttp } from "@perfectserve/http-ps";
import env from "@perfectserve/env-ps";
import { createEmailCredentialsService } from "../services/emailCredentialsService";
import { useXlsxParser } from "./useXlsxParser";
/**
 * Main hook for email credentials workflow
 */
export function useEmailCredentials() {
    const { get, post } = useHttp();
    const xlsxParser = useXlsxParser();
    // Get environment configuration
    const environment = env.environment || "unknown";
    const apiBase = env.cspApi || "";
    // Workflow state
    const [currentStep, setCurrentStep] = useState("upload");
    const [isProcessing, setIsProcessing] = useState(false);
    const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);
    const [processingError, setProcessingError] = useState(null);
    const [results, setResults] = useState(null);
    // Create service
    const service = useMemo(() => createEmailCredentialsService(get, post), [get, post]);
    // Parse file and move to processing step
    const parseFile = useCallback(async (file) => {
        const result = await xlsxParser.parseFile(file);
        if (!result) {
            // Parsing failed, error is set in xlsxParser
            return;
        }
        // Move to processing step automatically
        setCurrentStep("processing");
    }, [xlsxParser]);
    /**
     * Process a single user through the workflow
     */
    const processUser = useCallback(async (record) => {
        const updatedRecord = { ...record };
        try {
            // Step 1: Get IdentityId (if not provided in XLSX)
            if (!updatedRecord.identityId && updatedRecord.v5UserId) {
                const userInfo = await service.searchUser(apiBase, updatedRecord.v5UserId);
                if (!userInfo) {
                    updatedRecord.status = "failed";
                    updatedRecord.error = "User not found";
                    return updatedRecord;
                }
                updatedRecord.identityId = userInfo.identityId;
                updatedRecord.displayName = userInfo.displayName;
            }
            // If we still don't have an IdentityId, fail
            if (!updatedRecord.identityId) {
                updatedRecord.status = "failed";
                updatedRecord.error = "No IdentityId available";
                return updatedRecord;
            }
            // Step 2: Get PerfectServe UPN
            const upn = await service.getPerfectServeUpn(apiBase, updatedRecord.identityId);
            if (!upn) {
                updatedRecord.status = "skipped";
                updatedRecord.skipReason = "No perfectserve.net UPN found";
                return updatedRecord;
            }
            updatedRecord.upn = upn;
            // Step 3: Send credential email
            const emailSent = await service.sendCredentialEmail(apiBase, updatedRecord.identityId, upn);
            if (emailSent) {
                updatedRecord.status = "sent";
            }
            else {
                updatedRecord.status = "failed";
                updatedRecord.error = "Failed to send credential email";
            }
            return updatedRecord;
        }
        catch (err) {
            updatedRecord.status = "failed";
            updatedRecord.error = err instanceof Error ? err.message : "Unknown error";
            return updatedRecord;
        }
    }, [service, apiBase]);
    /**
     * Start processing all users
     */
    const startProcessing = useCallback(async () => {
        if (xlsxParser.records.length === 0) {
            return;
        }
        setIsProcessing(true);
        setProcessingError(null);
        setCurrentProcessingIndex(0);
        const processedRecords = [];
        let sent = 0;
        let skipped = 0;
        let failed = 0;
        try {
            for (let i = 0; i < xlsxParser.records.length; i++) {
                setCurrentProcessingIndex(i + 1);
                const record = xlsxParser.records[i];
                const processedRecord = await processUser(record);
                processedRecords.push(processedRecord);
                // Update counts
                if (processedRecord.status === "sent") {
                    sent++;
                }
                else if (processedRecord.status === "skipped") {
                    skipped++;
                }
                else if (processedRecord.status === "failed") {
                    failed++;
                }
            }
            // Create final results
            const finalResults = {
                totalUsers: processedRecords.length,
                sentCount: sent,
                skippedCount: skipped,
                failedCount: failed,
                records: processedRecords,
                timestamp: new Date().toISOString(),
            };
            setResults(finalResults);
            setCurrentStep("complete");
        }
        catch (err) {
            setProcessingError(err instanceof Error ? err.message : "Processing failed");
            setCurrentStep("error");
        }
        finally {
            setIsProcessing(false);
        }
    }, [xlsxParser.records, processUser]);
    /**
     * Reset workflow
     */
    const reset = useCallback(() => {
        setCurrentStep("upload");
        setIsProcessing(false);
        setCurrentProcessingIndex(0);
        setProcessingError(null);
        setResults(null);
        xlsxParser.clear();
    }, [xlsxParser]);
    // Calculate counts from results
    const sentCount = results?.sentCount || 0;
    const skippedCount = results?.skippedCount || 0;
    const failedCount = results?.failedCount || 0;
    return {
        currentStep,
        setCurrentStep,
        // File parsing
        records: xlsxParser.records,
        isParsing: xlsxParser.isParsing,
        parseError: xlsxParser.error,
        parseFile,
        // Processing
        isProcessing,
        currentProcessingIndex,
        totalRecords: xlsxParser.records.length,
        processingError,
        startProcessing,
        // Results
        results,
        sentCount,
        skippedCount,
        failedCount,
        // General
        environment,
        apiBase,
        reset,
    };
}
